import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { db, storage, functions } from "../firebase";
import {
  collection, query, orderBy, onSnapshot, doc, addDoc, updateDoc, getDoc, setDoc,
  serverTimestamp, increment, Timestamp, where, deleteField,
  writeBatch, runTransaction, limit, startAfter, getDocs
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  Send2, EmojiHappy, Messages2, ArrowUp2, ArrowDown2, ArrowLeft, SearchNormal1, Microphone2,
  More, Paperclip2, DocumentText, Location, Stop, Trash, Flag, Slash,
} from "iconsax-reactjs";
import { isPresenceOnline } from "../utils/presence";
import { mediaLabelFor, formatFileSize, buildReplyPayload, SOFT_DELETE_FIELDS, firstUrlIn, renderTextWithLinks, takeQueuedLeadChat } from "../utils/chatShared";
import { useWebVoiceRecorder } from "../utils/useWebVoiceRecorder";
import LinkPreviewCard from "./chat/LinkPreviewCard";
import ReportUserModal from "./chat/ReportUserModal";
import MediaCaptionModal from "./chat/MediaCaptionModal";
import EmojiPicker from "emoji-picker-react";
// The chat grammar (list, thread, bubbles, composer) is shared with the admin
// support console so both surfaces read as one product; dealer-only pieces
// live in DealerChat.css.
import "./ChatPanel.css";
import "./DealerChat.css";

const sendChatNotification = httpsCallable(functions, "sendChatNotification");
const draftKey = (chatId) => `chat_draft_${chatId}`;
// Live page size for the open conversation: only the newest N messages are
// subscribed in realtime; older history loads on demand via "Load earlier
// messages" so opening a long-running thread doesn't pull its entire history.
const MESSAGES_PAGE_SIZE = 40;

const timeMillis = (m) => m.time?.toMillis?.() ?? 0;

function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function getDateKey(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toDateString();
}

function formatDuration(millis) {
  const total = Math.floor((millis || 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// The composer textarea grows with its content up to this height, then scrolls.
const COMPOSER_MAX_HEIGHT = 160;

function autoGrow(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
}

// emoji-picker-react ships its own light and dark palettes; pick the one that
// matches the portal: the explicit toggle on <html> first, then the OS.
function resolvedTheme() {
  const set = document.documentElement.getAttribute("data-theme");
  if (set === "dark" || set === "light") return set;
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function ReadTicks() {
  return (
    <span className="chat-ticks">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 13l4 4L16 7" />
        <path d="M11 16l1 1L22 7" />
      </svg>
      <span className="sr-only">Read</span>
    </span>
  );
}

// Renders a message body with all the types the mobile app can send:
// deleted placeholders, reply quotes, photo albums, single photo/video,
// voice notes, documents, link previews, and captions alongside media.
function MessageContent({ msg, mine }) {
  if (msg.deleted) {
    return <p className="chat-deleted">This message was deleted</p>;
  }
  const bubbleUrl = !msg.mediaUrl && !(msg.mediaUrls && msg.mediaUrls.length) ? firstUrlIn(msg.text) : null;
  return (
    <>
      {msg.replyTo && (
        <div className="chat-quote">
          <span className="chat-quote-name">{msg.replyTo.senderName || "Reply"}</span>
          <span className="chat-quote-text">{msg.replyTo.text || mediaLabelFor(msg.replyTo.mediaType)}</span>
        </div>
      )}
      {msg.mediaUrls && msg.mediaUrls.length > 1 ? (
        <div className="chat-album">
          {msg.mediaUrls.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="chat-album-tile">
              <img src={url} alt={`Attachment ${i + 1}`} />
            </a>
          ))}
        </div>
      ) : msg.mediaUrl ? (
        msg.mediaType === "image" ? (
          <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="chat-media-link"><img src={msg.mediaUrl} alt="Attachment" className="chat-media-img" /></a>
        ) : msg.mediaType === "video" ? (
          <video src={msg.mediaUrl} controls className="chat-media-video" />
        ) : msg.mediaType === "audio" ? (
          <audio src={msg.mediaUrl} controls className="chat-media-audio" />
        ) : (
          <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="chat-doc">
            <span className="chat-doc-icon"><DocumentText size={20} variant="Linear" /></span>
            <span className="chat-doc-info">
              <span className="chat-doc-name">{msg.mediaName || "Document"}</span>
              {msg.fileSize ? <span className="chat-doc-size">{formatFileSize(msg.fileSize)}</span> : null}
            </span>
          </a>
        )
      ) : null}
      {bubbleUrl ? <LinkPreviewCard url={bubbleUrl} mine={mine} /> : null}
      {(msg.isPropertyCard || msg.propertyData) && msg.propertyData ? (
        <div className="chat-property">
          {msg.propertyData.image ? (
            <div className="chat-property-media">
              <img src={msg.propertyData.image} alt={msg.propertyData.title || "Property"} />
              {msg.propertyData.price ? (
                <span className="chat-property-price">
                  ₹{Number(msg.propertyData.price).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="chat-property-body">
            <h5 className="chat-property-title">
              {msg.propertyData.title || "Property inquiry"}
            </h5>
            {msg.propertyData.location ? (
              <p className="chat-property-loc">
                <Location size={12} variant="Bold" />
                {msg.propertyData.location}
              </p>
            ) : null}
            <span className="chip chip-primary">Property inquiry</span>
          </div>
        </div>
      ) : null}
      {msg.text ? <p className="chat-text">{renderTextWithLinks(msg.text)}</p> : null}
    </>
  );
}


export default function DealerChat({ user }) {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  // Live page (newest MESSAGES_PAGE_SIZE, realtime) + older pages fetched on
  // demand. Both kept oldest-first; olderMessages always precedes liveMessages.
  const [liveMessages, setLiveMessages] = useState([]);
  const [olderMessages, setOlderMessages] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  const messagesPaneRef = useRef(null);
  // Scroll bookkeeping: only auto-scroll for genuinely new messages (see the
  // scroll effect below), and only when the reader is already near the bottom.
  const isNearBottomRef = useRef(true);
  const prevNewestIdRef = useRef(null);
  // Ids already submitted for read-marking, so re-runs of the marking effect
  // (focus flips, snapshots racing the write) can't double-decrement the counter.
  const markedReadIdsRef = useRef(new Set());
  // Which chat the current liveMessages belong to. On an A→B switch, activeChat
  // flips to B before B's first snapshot replaces liveMessages, so the marking
  // effect must not act on A's stale docs against B's collection.
  const liveChatIdRef = useRef(null);
  // The previous live page, to catch messages that slide out of it
  const liveListRef = useRef([]);
  // The lead chat to open once it shows up in the chat list
  const pendingChatIdRef = useRef(null);
  // Which chat has already had its full-conversation backlog swept (once per open).
  const sweptChatIdRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  // ── Parity features (reply / delete / actions / search / voice / jump / report) ──
  const [replyingTo, setReplyingTo] = useState(null);
  const [menuMsgId, setMenuMsgId] = useState(null);
  const [msgSearch, setMsgSearch] = useState(false);
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [highlightMsgId, setHighlightMsgId] = useState(null);
  const [showJumpBtn, setShowJumpBtn] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const voice = useWebVoiceRecorder();
  const [isSendingVoice, setIsSendingVoice] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null); // File[] awaiting a caption
  const [pendingVoice, setPendingVoice] = useState(null); // { blob, url, durationMillis, ext } awaiting send
  const [drafts, setDrafts] = useState({}); // chatId -> unsent text (sidebar preview)
  const messageRefs = useRef({});
  const highlightTimerRef = useRef(null);
  // Presentation only: skeletons until the first snapshot of each listener lands.
  const [chatsLoading, setChatsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const composerRef = useRef(null);

  const messages = olderMessages.length > 0 ? [...olderMessages, ...liveMessages] : liveMessages;

  // Grow the composer with its text (and again when it remounts after voice).
  useLayoutEffect(() => {
    autoGrow(composerRef.current);
  }, [replyText, voice.isRecording, pendingVoice, activeChat]);

  // Close any open per-message actions menu when clicking elsewhere.
  useEffect(() => {
    if (!menuMsgId) return;
    const close = () => setMenuMsgId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuMsgId]);

  // Load persisted unsent drafts once (shown as "Draft: …" in the list).
  useEffect(() => {
    const map = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("chat_draft_")) {
        const val = localStorage.getItem(key);
        if (val) map[key.slice("chat_draft_".length)] = val;
      }
    }
    setDrafts(map);
  }, []);

  const saveDraft = (chatId, text) => {
    if (text.trim()) {
      localStorage.setItem(draftKey(chatId), text);
      setDrafts((d) => ({ ...d, [chatId]: text }));
    } else {
      localStorage.removeItem(draftKey(chatId));
      setDrafts((d) => { const n = { ...d }; delete n[chatId]; return n; });
    }
  };

  // Peer id for the open chat (the participant that isn't this dealer).
  const getPeerId = (chat) => chat?.participants?.find((p) => p !== user.uid);

  // Monitor document focus
  const [isWindowFocused, setIsWindowFocused] = useState(document.hasFocus());
  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Fetch chats where user.uid is a participant. Exclude this dealer's own
  // support thread (doc id === their uid, or 'admin' as a participant) so their
  // conversation with support doesn't masquerade as a client chat.
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "chats"),
      where("participants", "array-contains", user.uid)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        const isOwnSupportThread = d.id === user.uid || (data.participants || []).includes("admin");
        if (isOwnSupportThread) return;
        list.push({ id: d.id, ...data });
      });
      // Sort by updatedAt descending
      list.sort((a, b) => {
        const timeA = a.updatedAt?.seconds || 0;
        const timeB = b.updatedAt?.seconds || 0;
        return timeB - timeA;
      });
      setChats(list);
      setChatsLoading(false);
    }, (err) => { console.error("Dealer Chats fetch failed:", err); setChatsLoading(false); });

    return () => unsubscribe();
  }, [user]);

  // Reply / Chat on a lead (Leads, Listings, the Dashboard) queues that tenant
  // and switches here. Open their chat by id ({dealerUid}_{tenantUid}, sorted,
  // as the app's LeadCard builds it), never by name: two tenants can share one
  // ("GharSetu User"), and matching on it messaged the wrong person. A lead
  // with no chat yet gets one started, so the reply still reaches them.
  useEffect(() => {
    if (!user?.uid || chatsLoading) return;
    const queued = takeQueuedLeadChat();
    if (queued && queued.peerId !== user.uid) {
      const chatId = [user.uid, queued.peerId].sort().join("_");
      pendingChatIdRef.current = chatId;
      if (!chats.some((c) => c.id === chatId)) {
        startChatWith(chatId, queued.peerId, queued.name).catch((err) => {
          pendingChatIdRef.current = null;
          console.error("Couldn't start a chat with this lead:", err);
          alert("Couldn't open a chat with this lead. Please try again.");
        });
      }
    }
    if (pendingChatIdRef.current) {
      const found = chats.find((c) => c.id === pendingChatIdRef.current);
      if (found) {
        pendingChatIdRef.current = null;
        setActiveChat(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, chats, chatsLoading]);

  // A new conversation with a tenant, in the shape the app's
  // createOrGetConversation writes, unless one already exists.
  const startChatWith = async (chatId, peerId, peerName) => {
    const chatRef = doc(db, "chats", chatId);
    if ((await getDoc(chatRef)).exists()) return;
    const peerSnap = await getDoc(doc(db, "users", peerId));
    const peer = peerSnap.exists() ? peerSnap.data() : {};
    await setDoc(chatRef, {
      participants: [user.uid, peerId],
      participantDetails: {
        [user.uid]: { name: user.name || "Dealer", avatar: user.avatarUrl || "" },
        [peerId]: { name: peer.name || peerName || "Client", avatar: peer.avatarUrl || "" },
      },
      lastMessage: "Chat initiated",
      lastMessageTime: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      unreadCount: { [user.uid]: 0, [peerId]: 0 },
    });
  };

  // Fetch the live (newest MESSAGES_PAGE_SIZE) page for the active chat.
  // Older history is loaded on demand via loadOlderMessages, not upfront:
  // otherwise a long-running thread reloads its entire history every time
  // it's opened. Keyed on activeChat ONLY: read-marking lives in its own
  // effect below, so window focus changes don't tear down the listener and
  // wipe paged-in history.
  useEffect(() => {
    setLiveMessages([]); // clear stale messages so the switched-to chat never shows the old thread
    setOlderMessages([]);
    setHasMoreOlder(true);
    isNearBottomRef.current = true;
    prevNewestIdRef.current = null;
    markedReadIdsRef.current = new Set();
    sweptChatIdRef.current = null;
    liveListRef.current = [];
    setMessagesLoading(!!activeChat);

    if (!activeChat) {
      return;
    }

    const q = query(
      collection(db, "chats", activeChat.id, "messages"),
      orderBy("time", "desc"),
      limit(MESSAGES_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.reverse(); // desc (newest-first) query → ascending for display
      // The live query is the newest page only, so each new message pushes
      // the oldest live one out of it. Keep those in olderMessages: they're
      // still in the conversation, and without this they vanished from the
      // middle of the thread.
      const keptIds = new Set(list.map((m) => m.id));
      const oldestLive = list.length > 0 ? timeMillis(list[0]) : null;
      const slidOut = oldestLive === null
        ? []
        : liveListRef.current.filter((m) => !keptIds.has(m.id) && timeMillis(m) <= oldestLive);
      liveListRef.current = list;
      if (slidOut.length > 0) {
        setOlderMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...slidOut.filter((m) => !seen.has(m.id))];
        });
      }
      liveChatIdRef.current = activeChat.id;
      setLiveMessages(list);
      setMessagesLoading(false);
      if (list.length < MESSAGES_PAGE_SIZE) setHasMoreOlder(false);
    }, (err) => { console.error("Dealer Messages fetch failed:", err); setMessagesLoading(false); });

    return () => unsubscribe();
  }, [activeChat]);

  // Mark the customer's live-page messages read while the window is focused.
  // isRead is what the mobile app renders as blue ticks; status is the legacy
  // web-side field; markMessagesRead writes both. Separate from the
  // subscription so focus flips only re-run this cheap scan; markedReadIdsRef
  // keeps re-runs from re-submitting (and re-decrementing) ids whose writes
  // haven't reflected in a snapshot yet. The liveChatIdRef guard stops a stale
  // liveMessages array (from the previous chat) being marked against the
  // just-switched-to chat's collection.
  useEffect(() => {
    if (!activeChat || !isWindowFocused || liveChatIdRef.current !== activeChat.id) return;
    const unreadIds = liveMessages
      .filter((m) => (m.senderId || m.sender) !== user.uid
        && !(m.status === "read" && m.isRead)
        && !markedReadIdsRef.current.has(m.id))
      .map((m) => m.id);
    if (unreadIds.length === 0) return;
    unreadIds.forEach((id) => markedReadIdsRef.current.add(id));
    markMessagesRead(activeChat.id, unreadIds).catch((err) => console.error("Batch read update failed:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, isWindowFocused, liveMessages, user]);

  // One-shot sweep for unread customer messages beyond the live page: the
  // per-snapshot marking above only sees the newest MESSAGES_PAGE_SIZE, so a
  // larger unread backlog would leave the older messages (and their share of
  // unreadCount.{dealerUid}) stuck. Runs once per chat, the first time focused.
  useEffect(() => {
    if (!activeChat || !isWindowFocused || sweptChatIdRef.current === activeChat.id) return;
    const chatId = activeChat.id;
    sweptChatIdRef.current = chatId;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "chats", chatId, "messages"),
          where("isRead", "==", false),
          limit(300),
        ));
        const ids = [];
        snap.forEach((d) => {
          const data = d.data();
          if ((data.senderId || data.sender) !== user.uid && !markedReadIdsRef.current.has(d.id)) ids.push(d.id);
        });
        if (cancelled || ids.length === 0) return;
        ids.forEach((id) => markedReadIdsRef.current.add(id));
        await markMessagesRead(chatId, ids);
      } catch (err) {
        if (!cancelled) { sweptChatIdRef.current = null; console.error("Unread backlog sweep failed:", err); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, isWindowFocused, user]);

  // Load the next page of older history, preserving scroll position (the
  // pane would otherwise jump to the top as content is prepended above it).
  const loadOlderMessages = async () => {
    if (!activeChat || loadingOlder || !hasMoreOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const pane = messagesPaneRef.current;
    const prevScrollHeight = pane ? pane.scrollHeight : 0;
    try {
      const oldest = messages[0];
      const q = query(
        collection(db, "chats", activeChat.id, "messages"),
        orderBy("time", "desc"),
        startAfter(oldest.time),
        limit(MESSAGES_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const older = [];
      snap.forEach((d) => older.push({ id: d.id, ...d.data() }));
      if (older.length < MESSAGES_PAGE_SIZE) setHasMoreOlder(false);
      older.reverse();
      if (older.length > 0) {
        setOlderMessages((prev) => [...older, ...prev]);
        // Restore the reader's viewport: keep the same content under the
        // fold instead of snapping to the newly-prepended top.
        requestAnimationFrame(() => {
          if (pane) pane.scrollTop = pane.scrollHeight - prevScrollHeight;
        });
      }
    } catch (err) {
      console.error("Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Mark a batch of messages read and decrement (never hard-reset) the
  // dealer's own unread counter by however many were just marked: the
  // customer's own "new message" bump is a separate, unordered increment(1)
  // write to the same field, so a hard reset can lose a race and land after
  // it, stranding the badge at a stale non-zero count. A transaction re-reads
  // the live value so the decrement always applies on top of whatever the
  // customer last wrote, however the two writes are interleaved.
  const markMessagesRead = async (convId, unreadIds) => {
    const batch = writeBatch(db);
    unreadIds.forEach((msgId) => {
      batch.update(doc(db, "chats", convId, "messages", msgId), { status: "read", isRead: true });
    });
    await batch.commit();

    const chatDocRef = doc(db, "chats", convId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(chatDocRef);
      const data = snap.data();
      if (!data) return;
      const current = data.unreadCount?.[user.uid] || 0;
      tx.update(chatDocRef, { [`unreadCount.${user.uid}`]: Math.max(0, current - unreadIds.length) });
    });
  };

  // Auto-scroll only when a genuinely NEW message arrives (read-tick and other
  // in-place edits keep the same newest id, so they never move the view), and
  // only if the reader is already near the bottom, or the message is our own.
  useEffect(() => {
    const newest = liveMessages[liveMessages.length - 1];
    if (!newest) { prevNewestIdRef.current = null; return; }
    if (newest.id === prevNewestIdRef.current) return;
    prevNewestIdRef.current = newest.id;
    const isMine = (newest.senderId || newest.sender) === user.uid;
    if (isMine || isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      setUnseenCount((c) => c + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMessages]);

  const handlePaneScroll = () => {
    const pane = messagesPaneRef.current;
    if (!pane) return;
    const distanceFromBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 120;
    setShowJumpBtn(distanceFromBottom > 300);
    if (distanceFromBottom < 120) setUnseenCount(0);
  };

  const jumpToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setUnseenCount(0);
    setShowJumpBtn(false);
  };

  const jumpToMessage = (id) => {
    setMsgSearch(false);
    setMsgSearchQuery("");
    const el = messageRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightMsgId(id);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightMsgId(null), 1800);
  };

  // Live customer presence for the open chat (users/{uid}.online + lastSeen,
  // written by the app's usePresenceReporter).
  const [customerPresence, setCustomerPresence] = useState(null);
  useEffect(() => {
    const peerId = getPeerId(activeChat);
    if (!peerId) { setCustomerPresence(null); return; }
    return onSnapshot(doc(db, "users", peerId), (snap) => {
      setCustomerPresence(snap.exists() ? snap.data() : null);
    }, () => setCustomerPresence(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, user]);

  // The mobile app reads typing state from the shared `typing` map
  // (typing.{dealerUid}), same field the customer app writes for its own uid.
  const setDealerTypingStatus = async (isTyping) => {
    if (!activeChat) return;
    try { await updateDoc(doc(db, "chats", activeChat.id), { [`typing.${user.uid}`]: isTyping }); }
    catch (err) { console.error("Typing status error:", err); }
  };

  // Peer (customer) typing: the app writes typing.{customerUid}.
  const isPeerTyping = (chat) => {
    const peerId = getPeerId(chat);
    return !!(peerId && chat?.typing?.[peerId]);
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setReplyText(val);
    if (activeChat) {
      saveDraft(activeChat.id, val);
      if (!isTypingRef.current) { isTypingRef.current = true; setDealerTypingStatus(true); }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => { isTypingRef.current = false; setDealerTypingStatus(false); }, 3000);
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTypingRef.current) setDealerTypingStatus(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat]);

  useEffect(() => {
    setShowHeaderMenu(false);
    setReplyingTo(null);
    setMenuMsgId(null);
    setMsgSearch(false);
    setMsgSearchQuery("");
    setShowJumpBtn(false);
    setUnseenCount(0);
    setShowReportModal(false);
    setPendingMedia(null);
    setPendingVoice((pv) => { if (pv?.url) URL.revokeObjectURL(pv.url); return null; });
    setReplyText(activeChat ? (localStorage.getItem(draftKey(activeChat.id)) || "") : "");
  }, [activeChat]);

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!replyText.trim() || !activeChat || isBlocked) return;

    const textToSend = replyText.trim();
    setReplyText("");
    saveDraft(activeChat.id, "");
    const replyPayload = replyingTo ? buildReplyPayload(replyingTo, user.uid, getPeerDetails(active).name) : null;
    setReplyingTo(null);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    isTypingRef.current = false; setDealerTypingStatus(false);

    try {
      // 1. Add message doc. senderId is authoritative (matches the mobile
      // schema); sender kept for backward compatibility; isRead drives the
      // customer's read ticks.
      await addDoc(collection(db, "chats", activeChat.id, "messages"), {
        text: textToSend,
        senderId: user.uid,
        sender: user.uid,
        senderName: user.name || "Dealer",
        time: Timestamp.now(),
        status: "sent",
        isRead: false,
        replyTo: replyPayload,
      });

      // 2. Update conversation doc metadata
      const peerId = getPeerId(activeChat);
      const unreadCountField = `unreadCount.${peerId}`;

      await updateDoc(doc(db, "chats", activeChat.id), {
        lastMessage: textToSend,
        lastMessageTime: serverTimestamp(),
        lastSenderId: user.uid,
        updatedAt: serverTimestamp(),
        [unreadCountField]: increment(1)
      });

      // 3. Send Push Notification via callable cloud function
      sendChatNotification({
        recipientId: peerId,
        conversationId: activeChat.id,
        senderName: user.name || "Dealer",
        body: textToSend
      }).catch((e) => console.log("Push notification not supported or skipped:", e.message));

    } catch (err) {
      console.error("Message send failed:", err);
      setReplyText((cur) => cur || textToSend);
    }
  };

  // The composer is a textarea so long replies can wrap: Enter sends (as the
  // old single-line input's form submit did), Shift+Enter adds a new line.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // Base fields shared by every dealer-authored message doc.
  const dealerBaseMsg = () => ({
    senderId: user.uid,
    sender: user.uid,
    senderName: user.name || "Dealer",
    time: Timestamp.now(),
    status: "sent",
    isRead: false,
  });

  // Promise wrapper around a single resumable upload, driving the progress bar.
  const uploadBlob = (chatId, blob, fileName) => new Promise((resolve, reject) => {
    const storageRef = ref(storage, `chats/${chatId}/media/dealer_${Date.now()}_${fileName}`);
    const task = uploadBytesResumable(storageRef, blob);
    task.on("state_changed",
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => { try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (err) { reject(err); } });
  });

  const bumpConversation = async (chatId, label) => {
    const peerId = getPeerId(activeChat);
    await updateDoc(doc(db, "chats", chatId), {
      lastMessage: label, lastMessageTime: serverTimestamp(), lastSenderId: user.uid,
      updatedAt: serverTimestamp(), [`unreadCount.${peerId}`]: increment(1),
    });
    sendChatNotification({ recipientId: peerId, conversationId: chatId, senderName: user.name || "Dealer", body: label }).catch(() => {});
  };

  // Picking files opens the caption composer instead of sending immediately.
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (files.length === 0 || !activeChat || isBlocked) return;
    setPendingMedia(files);
  };

  const uploadPendingMedia = async (files, caption) => {
    if (!activeChat || !files || files.length === 0 || isBlocked) return;
    const chatId = activeChat.id;
    const images = files.filter((f) => f.type.startsWith("image/"));
    const others = files.filter((f) => !f.type.startsWith("image/"));
    let captionLeft = caption || "";

    setIsUploading(true); setUploadProgress(0);
    try {
      if (images.length > 1) {
        const urls = [];
        for (const f of images) urls.push(await uploadBlob(chatId, f, f.name));
        await addDoc(collection(db, "chats", chatId, "messages"), {
          ...dealerBaseMsg(), text: captionLeft, mediaUrl: urls[0], mediaUrls: urls, mediaType: "image", mediaName: images[0].name,
        });
        await bumpConversation(chatId, captionLeft || `📷 ${urls.length} Photos`);
        captionLeft = "";
      } else if (images.length === 1) {
        others.unshift(images[0]);
      }
      for (const file of others) {
        const mediaType = file.type.startsWith("image/") ? "image"
          : file.type.startsWith("video/") ? "video"
          : file.type.startsWith("audio/") ? "audio"
          : "document";
        const url = await uploadBlob(chatId, file, file.name);
        const thisCaption = captionLeft; captionLeft = "";
        await addDoc(collection(db, "chats", chatId, "messages"), {
          ...dealerBaseMsg(), text: thisCaption, mediaUrl: url, mediaType, mediaName: file.name, fileSize: file.size,
        });
        await bumpConversation(chatId, thisCaption || mediaLabelFor(mediaType));
      }
    } catch (err) {
      console.error("Upload failed:", err);
      alert("File upload failed.");
    } finally {
      setIsUploading(false); setUploadProgress(0); setPendingMedia(null);
    }
  };

  // ── Voice notes: record → preview → send (mirrors mobile) ──────────
  const handleStopRecording = async () => {
    const result = await voice.stop();
    if (!result || result.durationMillis < 800) return;
    setPendingVoice({ ...result, url: URL.createObjectURL(result.blob) });
  };

  const discardPendingVoice = () => {
    setPendingVoice((pv) => { if (pv?.url) URL.revokeObjectURL(pv.url); return null; });
  };

  const handleSendPendingVoice = async () => {
    if (!activeChat || !pendingVoice || isBlocked) return;
    const chatId = activeChat.id;
    const clip = pendingVoice;
    setIsSendingVoice(true);
    try {
      const fileName = `voice_${chatId}_${Date.now()}.${clip.ext}`;
      const url = await uploadBlob(chatId, clip.blob, fileName);
      await addDoc(collection(db, "chats", chatId, "messages"), {
        ...dealerBaseMsg(), text: "", mediaUrl: url, mediaType: "audio", mediaName: fileName, durationMillis: clip.durationMillis,
      });
      await bumpConversation(chatId, "🎤 Voice message");
      if (clip.url) URL.revokeObjectURL(clip.url);
      setPendingVoice(null);
    } catch (err) {
      console.error("Voice send failed:", err);
    } finally {
      setIsSendingVoice(false);
    }
  };

  // ── Per-message actions ─────────────────────────────────────────────
  const startReply = (msg) => { setReplyingTo(msg); setMenuMsgId(null); };
  const handleCopy = (msg) => { if (msg.text && navigator.clipboard) navigator.clipboard.writeText(msg.text).catch(() => {}); setMenuMsgId(null); };
  const handleDeleteMessage = async (msg) => {
    setMenuMsgId(null);
    if (!activeChat || !window.confirm("Delete this message for everyone?")) return;
    try {
      await updateDoc(doc(db, "chats", activeChat.id, "messages", msg.id), SOFT_DELETE_FIELDS);
    } catch (err) { console.error("Delete message failed:", err); }
  };

  // Append the picked emoji to the composer (and its draft); the full picker
  // stays open so several can be added in a row, closing on outside click.
  const handleEmojiSelect = (emoji) => {
    setReplyText((prev) => {
      const next = prev + emoji;
      if (activeChat) saveDraft(activeChat.id, next);
      return next;
    });
  };

  useEffect(() => {
    if (!showEmojiPicker) return;
    const close = () => setShowEmojiPicker(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [showEmojiPicker]);

  // Helper to extract peer details
  const getPeerDetails = (chat) => {
    if (!chat || !chat.participants) return { name: "Client", avatar: "" };
    const peerId = getPeerId(chat);
    if (chat.participantDetails && chat.participantDetails[peerId]) {
      return {
        name: chat.participantDetails[peerId].name || "Client",
        avatar: chat.participantDetails[peerId].avatar || ""
      };
    }
    // Fallback if details are flat fields (support chat)
    if (chat.customerId === peerId) {
      return {
        name: chat.customerName || "Client",
        avatar: chat.customerAvatar || ""
      };
    }
    return { name: "Client", avatar: "" };
  };

  // Filter conversations
  const filteredChats = chats.filter((c) => {
    const peer = getPeerDetails(c);
    return peer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (c.lastMessage || "").toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Live-updated view of the open chat: `activeChat` is only set at selection
  // time, so typing/presence/block changes must come from the subscribed list.
  const active = chats.find((c) => c.id === activeChat?.id) || activeChat;

  // Blocking (mirrors the mobile app + firestore.rules): while either side has
  // blocked the other, no one can send.
  const activePeerId = getPeerId(active);
  const iBlockedPeer = !!active?.blockedBy?.[user.uid];
  const peerBlockedMe = !!(activePeerId && active?.blockedBy?.[activePeerId]);
  const isBlocked = iBlockedPeer || peerBlockedMe;

  const handleToggleBlock = async () => {
    setShowHeaderMenu(false);
    if (!activeChat) return;
    const nextBlocked = !iBlockedPeer;
    if (nextBlocked && !window.confirm("Block this user? They won't be able to send you messages until you unblock them.")) return;
    try {
      await updateDoc(doc(db, "chats", activeChat.id), {
        [`blockedBy.${user.uid}`]: nextBlocked ? true : deleteField(),
      });
    } catch (err) {
      console.error("Failed to update block state:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleSubmitReport = async (reason, details) => {
    if (!activeChat || !activePeerId) return;
    try {
      await addDoc(collection(db, "reports"), {
        reporterId: user.uid,
        reportedUserId: activePeerId,
        conversationId: activeChat.id,
        reason,
        details: details || "",
        status: "open",
        createdAt: serverTimestamp(),
      });
      alert("Report submitted. Our team will review it.");
    } catch (err) {
      console.error("Failed to submit report:", err);
      alert("Could not submit the report. Please try again.");
    }
  };

  // In-conversation message search over loaded messages.
  const searchResults = msgSearchQuery.trim()
    ? messages.filter((m) => !m.deleted && m.text && m.text.toLowerCase().includes(msgSearchQuery.trim().toLowerCase()))
    : [];

  // Presence line for the header.
  const customerOnline = isPresenceOnline(customerPresence?.online, customerPresence?.lastSeen);
  const customerLastSeenLabel = (() => {
    if (customerOnline) return "Active now";
    const ls = customerPresence?.lastSeen;
    if (!ls?.toDate) return "Offline";
    const d = ls.toDate();
    const sameDay = d.toDateString() === new Date().toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return sameDay ? `last seen today at ${time}` : `last seen ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
  })();

  // Group messages with date separators.
  const messagesWithDates = [];
  let lastDateKey = "";
  messages.forEach((msg, idx) => {
    const dk = getDateKey(msg.time);
    if (dk && dk !== lastDateKey) {
      messagesWithDates.push({ type: "date", label: formatDateLabel(msg.time), key: `date-${dk}` });
      lastDateKey = dk;
    }
    // Last bubble in a run from one sender (or before a day break) gets the tail.
    const next = messages[idx + 1];
    const isLastInSequence = !next
      || (next.senderId || next.sender) !== (msg.senderId || msg.sender)
      || getDateKey(next.time) !== dk;
    messagesWithDates.push({ type: "msg", ...msg, isLastInSequence });
  });

  const activePeer = getPeerDetails(active);
  const peerAvatar = (details, className) => (
    <span className={`avatar ${className}`} aria-hidden="true">
      {details.avatar ? <img src={details.avatar} alt="" /> : getInitials(details.name)}
    </span>
  );

  return (
    <div className={`chat-shell ${activeChat ? "is-thread-open" : ""}`}>
      {/* Conversation list */}
      <section className="chat-list" aria-label="Conversations">
        <div className="chat-list-head">
          <div className="search-field">
            <SearchNormal1 size={16} variant="Linear" />
            <input
              type="text"
              className="input"
              placeholder="Search conversations"
              aria-label="Search conversations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {!chatsLoading && chats.length > 0 && (
            <span className="chat-list-meta">
              {chats.length} {chats.length === 1 ? "conversation" : "conversations"}
            </span>
          )}
        </div>

        <div className="chat-list-body">
          {chatsLoading ? (
            <div aria-busy="true">
              <span className="sr-only" role="status">Loading conversations</span>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="chat-row-skeleton" aria-hidden="true">
                  <span className="skeleton chat-sk-avatar" />
                  <span className="chat-sk-lines">
                    <span className="skeleton chat-sk-line" />
                    <span className="skeleton chat-sk-line is-long" />
                  </span>
                </div>
              ))}
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="empty-state chat-list-empty">
              <span className="empty-state-icon"><Messages2 size={22} variant="Linear" /></span>
              {searchQuery ? (
                <>
                  <h3>No matches</h3>
                  <p>No conversations match “{searchQuery}”. Search by name or by words in the last message.</p>
                </>
              ) : (
                <>
                  <h3>No conversations yet</h3>
                  <p>When tenants or buyers message you from the GharSetu app, their chats appear here.</p>
                </>
              )}
            </div>
          ) : (
            filteredChats.map((c) => {
              const peer = getPeerDetails(c);
              const isActive = activeChat && activeChat.id === c.id;
              const unreadCount = c.unreadCount && typeof c.unreadCount === "object" ? c.unreadCount[user.uid] || 0 : c.unreadCount || 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`chat-row ${isActive ? "is-active" : ""} ${unreadCount > 0 ? "is-unread" : ""}`}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => setActiveChat(c)}
                >
                  {peerAvatar(peer, "chat-row-avatar")}
                  <span className="chat-row-main">
                    <span className="chat-row-top">
                      <span className="chat-row-name">{peer.name}</span>
                      <span className="chat-row-time">
                        {c.lastMessageTime ? formatTime(c.lastMessageTime) : ""}
                      </span>
                    </span>
                    <span className="chat-row-bottom">
                      <span className="chat-row-preview">
                        {isPeerTyping(c)
                          ? <span className="chat-row-typing">Typing…</span>
                          : drafts[c.id]
                            ? <><span className="chat-row-draft">Draft:</span> {drafts[c.id]}</>
                            : (c.lastMessage || "No messages yet")}
                      </span>
                      {unreadCount > 0 && (
                        <span className="count-badge" aria-label={`${unreadCount} unread`}>{unreadCount > 99 ? "99+" : unreadCount}</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </section>

      {/* Thread */}
      <section className="chat-thread" aria-label={activeChat ? `Conversation with ${activePeer.name}` : "Conversation"}>
        {activeChat ? (
          <>
            <header className="chat-thread-head">
              <button type="button" className="btn-icon chat-back" onClick={() => setActiveChat(null)} aria-label="Back to conversations">
                <ArrowLeft size={20} variant="Linear" />
              </button>
              {peerAvatar(activePeer, "chat-head-avatar")}
              <div className="chat-head-text">
                <h2 className="chat-head-name">{activePeer.name}</h2>
                <p className="chat-head-sub">
                  <span className={`chat-presence ${customerOnline ? "is-online" : ""}`} aria-hidden="true" />
                  {isPeerTyping(active) ? "Typing…" : customerLastSeenLabel}
                </p>
              </div>
              <div className="chat-head-actions">
                <button
                  type="button"
                  className="btn-icon"
                  aria-pressed={msgSearch}
                  aria-label="Search in conversation"
                  title="Search in conversation"
                  onClick={() => { setMsgSearch((v) => !v); setMsgSearchQuery(""); }}
                >
                  <SearchNormal1 size={18} variant="Linear" />
                </button>
                <div className="dc-options">
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => setShowHeaderMenu((v) => !v)}
                    aria-label="Conversation options"
                    aria-haspopup="menu"
                    aria-expanded={showHeaderMenu}
                    title="Conversation options"
                  >
                    <More size={18} variant="Linear" />
                  </button>
                  {showHeaderMenu && (
                    <div className="chat-menu dc-options-menu" role="menu">
                      <button type="button" role="menuitem" onClick={handleToggleBlock}>
                        <Slash size={16} variant="Linear" />
                        {iBlockedPeer ? "Unblock user" : "Block user"}
                      </button>
                      <button type="button" role="menuitem" className="is-danger" onClick={() => { setShowHeaderMenu(false); setShowReportModal(true); }}>
                        <Flag size={16} variant="Linear" />
                        Report user
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            {msgSearch && (
              <div className="chat-find">
                <div className="search-field">
                  <SearchNormal1 size={16} variant="Linear" />
                  <input
                    type="text"
                    className="input"
                    autoFocus
                    placeholder="Search in this conversation"
                    aria-label="Search in this conversation"
                    value={msgSearchQuery}
                    onChange={(e) => setMsgSearchQuery(e.target.value)}
                  />
                </div>
                <button type="button" className="btn-icon" onClick={() => { setMsgSearch(false); setMsgSearchQuery(""); }} aria-label="Close search">
                  <CloseIcon />
                </button>
              </div>
            )}
            {msgSearch && msgSearchQuery.trim() && (
              <div className="chat-find-results">
                {searchResults.length === 0 ? (
                  <p className="chat-find-empty">No messages found for “{msgSearchQuery.trim()}”.</p>
                ) : (
                  searchResults.map((m) => (
                    <button key={m.id} type="button" className="chat-find-result" onClick={() => jumpToMessage(m.id)}>
                      <span className="chat-find-who">{(m.senderId || m.sender) === user.uid ? "You" : activePeer.name}</span>
                      <span className="chat-find-text">{m.text}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            <div className="chat-thread-main">
              <div className="chat-scroll">
                <div className="chat-messages" ref={messagesPaneRef} onScroll={handlePaneScroll}>
                  {hasMoreOlder && messages.length > 0 && (
                    <div className="chat-older">
                      <button type="button" className="btn-secondary btn-sm" onClick={loadOlderMessages} disabled={loadingOlder}>
                        {loadingOlder ? <><span className="btn-spinner" /> Loading</> : <><ArrowUp2 size={14} variant="Linear" /> Load earlier messages</>}
                      </button>
                    </div>
                  )}
                  {messagesLoading ? (
                    <div className="chat-msgs-skeleton" aria-busy="true">
                      <span className="sr-only" role="status">Loading messages</span>
                      <span className="skeleton chat-sk-bubble is-theirs is-lg" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-theirs is-sm" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-mine is-md" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-theirs is-md" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-mine is-lg" aria-hidden="true" />
                    </div>
                  ) : messagesWithDates.length === 0 ? (
                    <div className="empty-state chat-thread-empty">
                      <span className="empty-state-icon"><Messages2 size={22} variant="Linear" /></span>
                      <h3>No messages yet</h3>
                      <p>Type a message below to start chatting.</p>
                    </div>
                  ) : (
                    messagesWithDates.map((item) => {
                      if (item.type === "date") {
                        return <div key={item.key} className="chat-day"><span>{item.label}</span></div>;
                      }
                      const m = item;
                      const isMine = (m.senderId || m.sender) === user.uid;
                      const menuOpen = menuMsgId === m.id;
                      return (
                        <div
                          key={m.id}
                          ref={(el) => { if (el) messageRefs.current[m.id] = el; }}
                          className={`chat-msg ${isMine ? "is-mine" : "is-theirs"} ${m.isLastInSequence ? "is-tail" : ""} ${highlightMsgId === m.id ? "is-flash" : ""}`}
                        >
                          <div className="chat-bubble">
                            <MessageContent msg={m} mine={isMine} />
                            <span className="chat-meta">
                              {m.time ? formatTime(m.time) : ""}
                              {isMine && (m.status === "read" || m.isRead) && <ReadTicks />}
                            </span>
                          </div>
                          {!m.deleted && (
                            <div className={`chat-msg-tools ${menuOpen ? "is-open" : ""}`}>
                              <button
                                type="button"
                                className="chat-msg-more"
                                aria-label="Message actions"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                onClick={(e) => { e.stopPropagation(); setMenuMsgId(menuOpen ? null : m.id); }}
                              >
                                <More size={16} variant="Linear" />
                              </button>
                              {menuOpen && (
                                <div className="chat-menu chat-msg-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                                  <button type="button" role="menuitem" onClick={() => startReply(m)}>Reply</button>
                                  {m.text ? <button type="button" role="menuitem" onClick={() => handleCopy(m)}>Copy</button> : null}
                                  {isMine ? <button type="button" role="menuitem" className="is-danger" onClick={() => handleDeleteMessage(m)}>Delete</button> : null}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  {isPeerTyping(active) && (
                    <div className="chat-msg is-theirs is-tail">
                      <div className="chat-bubble chat-typing">
                        <span className="sr-only">{activePeer.name} is typing</span>
                        <span className="chat-typing-dots" aria-hidden="true"><i /><i /><i /></span>
                      </div>
                    </div>
                  )}
                  {isUploading && (
                    <div className="chat-msg is-mine is-tail">
                      <div className="chat-bubble chat-uploading" role="status">
                        <p className="chat-text">Uploading {uploadProgress}%</p>
                        <span className="chat-progress" aria-hidden="true"><span style={{ width: `${uploadProgress}%` }} /></span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {showJumpBtn && (
                  <button
                    type="button"
                    className="chat-jump"
                    onClick={jumpToBottom}
                    aria-label={unseenCount > 0 ? `Jump to latest, ${unseenCount} new` : "Jump to latest"}
                  >
                    <ArrowDown2 size={18} variant="Linear" />
                    {unseenCount > 0 && <span className="count-badge chat-jump-count" aria-hidden="true">{unseenCount > 99 ? "99+" : unseenCount}</span>}
                  </button>
                )}
              </div>
            </div>

            {/* Composer, or the blocked notice while either side has blocked the other */}
            <div className="chat-composer">
              {isBlocked ? (
                <div className="dc-blocked" role="status">
                  <span>
                    {iBlockedPeer
                      ? "You blocked this user. Unblock to continue the conversation."
                      : "This user is no longer receiving your messages."}
                  </span>
                  {iBlockedPeer && (
                    <button type="button" className="btn-tonal btn-sm" onClick={handleToggleBlock}>
                      Unblock
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {replyingTo && (
                    <div className="chat-replying">
                      <div className="chat-replying-body">
                        <span className="chat-replying-name">{(replyingTo.senderId || replyingTo.sender) === user.uid ? "You" : activePeer.name}</span>
                        <span className="chat-replying-text">{replyingTo.text || mediaLabelFor(replyingTo.mediaType)}</span>
                      </div>
                      <button type="button" className="btn-icon" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
                        <CloseIcon />
                      </button>
                    </div>
                  )}

                  {voice.isRecording ? (
                    <div className="chat-composer-bar chat-voice">
                      <button type="button" className="btn-icon chat-voice-discard" onClick={() => voice.cancel()} aria-label="Cancel recording">
                        <Trash size={18} variant="Linear" />
                      </button>
                      <span className="chat-voice-dot" aria-hidden="true" />
                      <span className="chat-voice-timer" role="timer" aria-label="Recording time">{formatDuration(voice.durationMillis)}</span>
                      <button type="button" className="chat-send" onClick={handleStopRecording} aria-label="Stop recording">
                        <Stop size={16} variant="Bold" />
                      </button>
                    </div>
                  ) : pendingVoice ? (
                    <div className="chat-composer-bar chat-voice">
                      <button type="button" className="btn-icon chat-voice-discard" onClick={discardPendingVoice} disabled={isSendingVoice} aria-label="Discard recording">
                        <Trash size={18} variant="Linear" />
                      </button>
                      <audio src={pendingVoice.url} controls className="chat-voice-preview" />
                      <button type="button" className="chat-send" onClick={handleSendPendingVoice} disabled={isSendingVoice} aria-label="Send voice message">
                        {isSendingVoice ? <span className="btn-spinner" /> : <Send2 size={18} variant="Bold" />}
                      </button>
                    </div>
                  ) : (
                    <form className="chat-composer-bar" onSubmit={handleSend}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        onChange={handleFileSelect}
                        multiple
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      />
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => fileInputRef.current.click()}
                        aria-label="Attach files"
                        title="Attach files"
                      >
                        <Paperclip2 size={20} variant="Linear" />
                      </button>

                      <div className="chat-emoji" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          aria-label="Add emoji"
                          aria-expanded={showEmojiPicker}
                          title="Add emoji"
                        >
                          <EmojiHappy size={20} variant="Linear" />
                        </button>
                        {showEmojiPicker && (
                          <div className="chat-emoji-popover">
                            <EmojiPicker
                              onEmojiClick={(data) => handleEmojiSelect(data.emoji)}
                              width={320}
                              height={380}
                              theme={resolvedTheme()}
                              previewConfig={{ showPreview: false }}
                              lazyLoadEmojis
                            />
                          </div>
                        )}
                      </div>

                      <textarea
                        ref={composerRef}
                        className="chat-composer-input"
                        value={replyText}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        placeholder={isUploading ? "Uploading…" : "Type a message"}
                        aria-label="Message"
                        disabled={isUploading}
                        rows={1}
                      />

                      {!replyText.trim() && voice.supported && (
                        <button type="button" className="btn-icon" onClick={() => voice.start()} disabled={isUploading} aria-label="Record voice message" title="Record voice message">
                          <Microphone2 size={20} variant="Linear" />
                        </button>
                      )}
                      <button type="submit" className="chat-send" disabled={isUploading || !replyText.trim()} aria-label="Send message">
                        <Send2 size={18} variant="Bold" />
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>

            <ReportUserModal
              visible={showReportModal}
              userName={activePeer.name}
              onClose={() => setShowReportModal(false)}
              onSubmit={handleSubmitReport}
            />

            {pendingMedia && (
              <MediaCaptionModal
                files={pendingMedia}
                uploading={isUploading}
                progress={uploadProgress}
                onSend={(caption) => uploadPendingMedia(pendingMedia, caption)}
                onCancel={() => setPendingMedia(null)}
              />
            )}
          </>
        ) : (
          <div className="empty-state chat-thread-placeholder">
            <span className="empty-state-icon"><Messages2 size={24} variant="Linear" /></span>
            <h3>Select a conversation</h3>
            <p>Choose a chat from the list to view messages and respond to client inquiries.</p>
          </div>
        )}
      </section>
    </div>
  );
}
